import { Component, OnInit } from '@angular/core';
import { Select, Store } from '@ngxs/store';
import { isEqual, pick, startCase } from 'lodash-es';
import { MessageService } from 'primeng/api';
import { Observable, pairwise, startWith } from 'rxjs';
import { EnvService } from 'src/app/core/services';
import { OrderWizardStatus } from 'src/app/core/store/order-wizard/order-wizard-data/order-wizard-data.model';
import { OrderWizardDataState } from 'src/app/core/store/order-wizard/order-wizard-data/order-wizard-data.state';
import { OrderWizardSubmissionState } from 'src/app/core/store/order-wizard/order-wizard-submission/order-wizard-submission.state';

@Component({
  selector: 'app-wizard-debug-info',
  templateUrl: './wizard-debug-info.component.html',
  styleUrls: ['./wizard-debug-info.component.scss'],
})
export class WizardDebugInfoComponent implements OnInit {
  @Select(OrderWizardSubmissionState.submitToAgent) submitToAgent$!: Observable<OrderWizardStatus>;
  @Select(OrderWizardSubmissionState.sendToDo) sendToDo$!: Observable<OrderWizardStatus>;
  @Select(OrderWizardDataState.canAutoComputeTargetShipDate)
  canAutoComputeTargetShipDate$!: Observable<OrderWizardStatus>;
  @Select(OrderWizardDataState.hideSummaryStepBillingInfo)
  hideSummaryStepBillingInfo$!: Observable<OrderWizardStatus>;
  @Select(OrderWizardDataState.skipBillingStep) skipBillingStep$!: Observable<OrderWizardStatus>;
  @Select(OrderWizardDataState.exitOrderWizard) exitOrderWizard$!: Observable<OrderWizardStatus>;
  @Select(OrderWizardDataState.doNotFetchCopay) doNotFetchCopay$!: Observable<OrderWizardStatus>;
  @Select(OrderWizardSubmissionState.shouldSetCollectionStatus)
  shouldSetCollectionStatus$!: Observable<OrderWizardStatus>;

  isProduction = this.envService.isProduction();
  showDebug = false;
  isInitiated = false;

  constructor(private store: Store, private envService: EnvService, private messageService: MessageService) {}

  ngOnInit(): void {
    if (!this.isProduction && this.showDebug) {
      this.initDebugInfo();
    }
  }

  formatDebugInfo(debugInfo: any): string {
    return JSON.stringify(debugInfo, null, 1)
      .replace(/\{\n|\n\}/g, '')
      .replace(/\"(.*)\"/g, (match, jsonStr) => startCase(jsonStr))
      .replace(/true/g, 'Yes')
      .replace(/false/g, 'No');
  }

  showMessage(title: string, debugInfo: any) {
    this.messageService.add({
      key: 'orderWizardDebugMessages',
      summary: title,
      detail: this.formatDebugInfo(debugInfo),
      closable: true,
    });
  }

  areStatusesEqual(prev: OrderWizardStatus, next: OrderWizardStatus) {
    return isEqual(pick(prev, ['value', 'debugInfo']), pick(next, ['value', 'debugInfo']));
  }

  initDebugInfo() {
    if (this.isInitiated) return;

    this.submitToAgent$.pipe(startWith({ value: true, debugInfo: null }), pairwise()).subscribe(([prev, next]) => {
      if (!this.areStatusesEqual(prev, next)) {
        this.showMessage(`Submit to Agent? ${next.value ? 'Yes' : 'No'}`, next.debugInfo);
      }
    });

    this.sendToDo$.pipe(startWith({ value: false, debugInfo: null }), pairwise()).subscribe(([prev, next]) => {
      if (!this.areStatusesEqual(prev, next)) {
        this.showMessage(`Send To Do? ${next.value ? 'Yes' : 'No'}`, next.debugInfo);
      }
    });

    this.canAutoComputeTargetShipDate$
      .pipe(startWith({ value: false, debugInfo: null }), pairwise())
      .subscribe(([prev, next]) => {
        if (!this.areStatusesEqual(prev, next)) {
          this.showMessage(`Can Auto-Compute Target Ship Date? ${next.value ? 'Yes' : 'No'}`, next.debugInfo);
        }
      });

    this.doNotFetchCopay$.pipe(startWith({ value: false, debugInfo: null }), pairwise()).subscribe(([prev, next]) => {
      if (!this.areStatusesEqual(prev, next)) {
        this.showMessage(`Should not fetch copay? ${next.value ? 'Yes' : 'No'}`, next.debugInfo);
      }
    });

    this.hideSummaryStepBillingInfo$
      .pipe(startWith({ value: false, debugInfo: null }), pairwise())
      .subscribe(([prev, next]) => {
        if (!this.areStatusesEqual(prev, next)) {
          this.showMessage(`Hide Summary Step Billing Info? ${next.value ? 'Yes' : 'No'}`, next.debugInfo);
        }
      });

    this.skipBillingStep$.pipe(startWith({ value: false, debugInfo: null }), pairwise()).subscribe(([prev, next]) => {
      if (!this.areStatusesEqual(prev, next)) {
        this.showMessage(`Skip Billing Step? ${next.value ? 'Yes' : 'No'}`, next.debugInfo);
      }
    });

    this.shouldSetCollectionStatus$
      .pipe(startWith({ value: false, debugInfo: null }), pairwise())
      .subscribe(([prev, next]) => {
        if (!this.areStatusesEqual(prev, next)) {
          this.showMessage(`Should Set Collection Status? ${next.value ? 'Yes' : 'No'}`, next.debugInfo);
        }
      });

    this.exitOrderWizard$.pipe(startWith({ value: false, debugInfo: null }), pairwise()).subscribe(([prev, next]) => {
      if (!this.areStatusesEqual(prev, next)) {
        this.showMessage(`Exit Order Wizard? ${next.value ? 'Yes' : 'No'}`, next.debugInfo);
      }
    });

    this.isInitiated;
  }

  hideDebugContent() {
    this.showDebug = false;
  }

  showDebugContent() {
    this.showDebug = true;
    if (!this.isInitiated) {
      this.initDebugInfo();
    }
  }
}
